import { useState, useEffect } from "react";
import { GenericField } from "../reportConfigs";

type Props = {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: Record<string, string>) => void;
  fields: GenericField[];
  initialData?: Record<string, any>;
  title: string;
};

const AddEditGroupModal = ({ isOpen, onClose, onSubmit, fields, initialData, title }: Props) => {
  const [form, setForm] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, boolean>>({});

  useEffect(() => {
    const initial: Record<string, string> = {};
    fields.forEach((f) => { initial[f.key] = initialData?.[f.key] ?? ""; });
    setForm(initial);
    setErrors({});
  }, [initialData, isOpen]);

  if (!isOpen) return null;

  const handleSubmit = () => {
    const newErrors: Record<string, boolean> = {};

    fields.forEach((f) => {
      if (f.required && !form[f.key]?.trim()) newErrors[f.key] = true;
      if ((form[f.key]?.length ?? 0) > 195) newErrors[f.key] = true;
    });

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setErrors({});
    setLoading(true);
    onSubmit(form);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
        <h2 className="text-lg font-semibold text-orange-500 mb-4">{title}</h2>

        <div className="flex flex-col gap-3">
          {fields.map((field) => (
            <div key={field.key} className="flex flex-col gap-1">
              <label className="text-sm font-medium text-gray-600">
                {field.label} {field.required && <span className="text-red-500">*</span>}
              </label>

              {field.type === "textarea" ? (
                <textarea
                  rows={3}
                  value={form[field.key] ?? ""}
                  onChange={(e) => {
                    setForm((p) => ({ ...p, [field.key]: e.target.value }));
                    if (errors[field.key]) setErrors((prev) => ({ ...prev, [field.key]: false }));
                    if ((form[field.key]?.length ?? 0) > 195) setErrors((prev) => ({ ...prev, [field.key]: true }));
                  }}
                  className={`border rounded px-2 py-1 text-sm resize-none focus:outline-none focus:ring-1 focus:ring-orange-400 ${
                    errors[field.key] ? "border-red-500" : "border-gray-300"
                  }`}
                />
              ) : (
                <input
                  type="text"
                  value={form[field.key] ?? ""}
                  onChange={(e) => {
                    setForm((p) => ({ ...p, [field.key]: e.target.value }));
                    if (errors[field.key]) setErrors((prev) => ({ ...prev, [field.key]: false }));
                    if ((form[field.key]?.length ?? 0) > 195) setErrors((prev) => ({ ...prev, [field.key]: true }));
                  }}
                  className={`border rounded px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-orange-400 ${
                    errors[field.key] ? "border-red-500" : "border-gray-300"
                  }`}
                />
              )}

              {errors[field.key] && (
                <span className="text-xs text-red-500">
                  {!form[field.key]?.trim() ? "This field is required" : "Maximum 195 characters allowed"}
                </span>
              )}
            </div>
          ))}
        </div>

        <div className="flex justify-end gap-2 mt-6">
          <button onClick={onClose} className="px-4 py-1.5 text-sm border border-gray-300 rounded hover:bg-gray-50">
            Cancel
          </button>
          <button disabled={loading} onClick={handleSubmit} className="px-4 py-1.5 text-sm bg-orange-400 text-white rounded hover:bg-orange-500">
            {loading ? <div className="flex">Please wait..  <div className={` ml-[6px] w-5 h-5 border-2 border-[#812d00] border-t-transparent rounded-full animate-spin`}></div></div> : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddEditGroupModal;